/**
 * Pro plan on the web (docs/MOBILE_APP_PLAN.md, M0).
 *
 * The API gates every LLM route: 402 ``subscription_required`` for free users
 * (with ``reason`` ``pro_feature``, ``free_quota`` or ``upload_quota``) and 429 ``fair_use`` for
 * Pro users over a daily cap. ``api.ts`` calls ``handleGateResponse`` for every
 * failed response; it opens the paywall dialog or shows the limit toast.
 */

/** ``history_import`` is client-only: a free user asked to import their ride history. */
export type PaywallReason = 'pro_feature' | 'free_quota' | 'upload_quota' | 'history_import';

export const PAYWALL_EVENT = 'paywall:show';
export const FAIR_USE_EVENT = 'plan:fair-use';

interface GateBody {
  code?: string;
  reason?: string;
  message?: string;
}

// The call that hit the gate usually adds its own "failed" toast right after;
// the paywall or limit message already explains it, so that toast is skipped.
const SUPPRESS_ERROR_TOASTS_MS = 2_000;
let lastGateAt = 0;

export function gateJustHandled(now: number = Date.now()): boolean {
  return now - lastGateAt < SUPPRESS_ERROR_TOASTS_MS;
}

function gateReason(reason: string | undefined): PaywallReason {
  return reason === 'free_quota' || reason === 'upload_quota' ? reason : 'pro_feature';
}

export function showPaywall(reason: PaywallReason = 'pro_feature'): void {
  globalThis.dispatchEvent(new CustomEvent<PaywallReason>(PAYWALL_EVENT, { detail: reason }));
}

/**
 * React to a 402 / 429 ``fair_use`` body. Returns true when it was one of
 * them (the caller should treat the request as handled, not as an error).
 */
export function handleGateResponse(status: number | undefined, body: unknown): boolean {
  const data = (body ?? {}) as GateBody;
  if (status === 402 && data.code === 'subscription_required') {
    lastGateAt = Date.now();
    showPaywall(gateReason(data.reason));
    return true;
  }
  if (status === 429 && data.code === 'fair_use') {
    lastGateAt = Date.now();
    globalThis.dispatchEvent(new CustomEvent(FAIR_USE_EVENT));
    return true;
  }
  return false;
}

/** Thrown by fetch-based callers (chat streams) when the gate answered. */
export class GateError extends Error {
  constructor() {
    super('plan_gate');
    this.name = 'GateError';
  }
}
